import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { deriveCapabilities, planDelCatalogo } from '../lib/planAccess';
import { limpiarLoDeLaPersona } from '../lib/almacenLocal';
import { aplicarAjustesDeFicha } from '../lib/ajustesEnFicha';
import PuertaCerrada from '../components/PuertaCerrada';
import { anotar } from '../lib/rastro';
import { ABIERTO_SIN_ACCESO } from '../lib/puertasDelCliente';
import { esCuentaDeBaja, apuntarCuentaDeBaja } from '../lib/cuentaDeBaja';
import { dispositivoRecordado, recordarDispositivo } from '../lib/segundoFactor';
import { fijarMinutosPorDelante } from '../lib/horaEspana';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    // LA FECHA DE UNA CUENTA DE PRUEBA (prueba 0.2, 02-11): el reloj de la app va lo que
    // vaya por delante esta cuenta, igual que el del servidor. Aquí, en el render y no
    // en un efecto: así ninguna pantalla se pinta antes con el día de verdad. Sin usuario, 0.
    fijarMinutosPorDelante(user?.minutos_por_delante);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    // NO ES LO MISMO NO TENER PERFIL QUE NO PODER CARGARLO.
    // Los dos acababan en `setProfile(null)`, así que un 500 del servidor dejaba al cliente
    // navegando como si nunca hubiera contratado nada: sin plan, sin acceso y sin una sola
    // pista de que algo había fallado. En producción le pasaba a un cliente en las 15 cargas
    // seguidas (QA del 15-08). Un 404 sí es «todavía no tiene perfil», y ese es normal.
    const [perfilNoCargado, setPerfilNoCargado] = useState(false);
    const [planCatalog, setPlanCatalog] = useState({});
    // Ajustes globales de la app (doc 16-08): los interruptores de las pantallas nuevas
    // y la frase del día. Se editan en el panel sin desplegar; ver routes/settings.py.
    const [appSettings, setAppSettings] = useState(null);

    // LA APP CERRADA AL PÚBLICO (14-09). El servidor contesta 503 con su frase y aquí se
    // guarda para pintarla a pantalla completa. Null = abierta, que es lo normal.
    const [puertaCerrada, setPuertaCerrada] = useState(null);

    // ACTUAR COMO UN CLIENTE (punto 4.11). Cuando el entrenador abre la calculadora de un
    // cliente desde su ficha, la app entera trabaja como ese cliente: cada petición lleva la
    // cabecera y el backend resuelve el usuario ahí (ver core/actuar_como.py). Así funcionan
    // las dietas, el reparto, el buscador, el sugeridor y el recetario sin duplicar media API.
    //
    // Vive en sessionStorage y no en localStorage a propósito: se acaba al cerrar la pestaña.
    // Quedarse "actuando como" otra persona sin darte cuenta, y encima entre reinicios del
    // navegador, es exactamente lo que no puede pasar.
    const actuandoComo = (() => {
        try { return JSON.parse(sessionStorage.getItem('actuar_como') || 'null'); }
        catch { return null; }
    })();

    // EL CLIENTE HTTP SE CREA UNA VEZ, NO EN CADA RENDER.
    //
    // Esto era un `axios.create` suelto en el cuerpo del componente, así que cada vez que el
    // proveedor se repintaba nacía un cliente nuevo. Media aplicación pide sus datos con
    // `useEffect(..., [api])`, y con un `api` distinto cada vez esos efectos se vuelven a
    // disparar: «Mis macros» pedía su historial TRES veces al abrirse, y el perfil, los planes
    // y los contadores de avisos, dos cada uno. Dieciséis peticiones para pintar una pantalla,
    // y 6,1 segundos con la rueda girando. Medido el 10-08 al hacer el catálogo.
    //
    // Las dependencias son las dos cosas que de verdad cambian la petición: el token y el
    // usuario que se está suplantando. `actuandoComo` es un objeto que se reconstruye en cada
    // render, así que se depende de su id, que es un texto y sí se puede comparar.
    const actuandoComoId = actuandoComo?.userId || null;
    const api = useMemo(() => {
        const instancia = axios.create({
            baseURL: API,
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(actuandoComoId ? { 'X-Actuar-Como': actuandoComoId } : {}),
            },
        });

        // La pantalla desde la que se pidió, para el rastro de fallos (core/rastro.py).
        instancia.interceptors.request.use((config) => {
            config.headers = config.headers || {};
            config.headers['X-Pantalla'] = window.location.pathname;
            return config;
        });

        instancia.interceptors.response.use(
            (response) => {
                // Si vuelve a contestar, es que la puerta se abrió: se quita la pantalla
                // y la app sigue donde estaba, sin volver a iniciar sesión.
                setPuertaCerrada((antes) => (antes ? null : antes));
                return response;
            },
            (error) => {
                // NI LLEGÓ AL SERVIDOR: sin conexión o servidor caído. El servidor no lo
                // puede apuntar, así que lo apunta la pantalla (lib/rastro).
                if (!error.response && !axios.isCancel(error)) {
                    anotar({
                        tipo: 'sin_respuesta',
                        mensaje: error.message,
                        detalle: { metodo: error.config?.method, url: error.config?.url },
                    });
                }
                // LA APP CERRADA AL PÚBLICO (14-09). Va lo primero: un 503 no es culpa del
                // cliente ni de su sesión, así que ni se le echa fuera ni se le cambia de
                // pantalla. Se le enseña la frase que manda el servidor, que es la misma
                // esté donde esté (`backend/core/puerta.py`).
                if (error.response?.status === 503) {
                    const dice = error.response?.data?.detail;
                    setPuertaCerrada(typeof dice === 'string' && dice ? dice : true);
                    return Promise.reject(error);
                }
                if (error.response?.status === 401) {
                    const url = error.config?.url || '';
                    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/segundo-factor');
                    const hadToken = !!localStorage.getItem('token');
                    // Solo avisar de sesión expirada si el usuario estaba logueado y no es un
                    // intento de login/registro (esos muestran su propio error). El id evita
                    // que varios 401 simultáneos apilen el mismo aviso.
                    if (hadToken && !isAuthEndpoint) {
                        toast.error('Tu sesión ha expirado. Vuelve a iniciar sesión.', { id: 'session-expired' });
                    }
                    logout();
                }
                // LE HAN DADO DE BAJA CON LA APP ABIERTA (14-10, BAJ-12): sale, pero a la
                // pantalla de entrada le queda el recado para decirle por qué. El 403 de
                // entrar ya lo explica esa pantalla por su cuenta.
                if (esCuentaDeBaja(error) && !(error.config?.url || '').includes('/auth/login')) {
                    const teniaSesion = !!localStorage.getItem('token');
                    logout();
                    if (teniaSesion) apuntarCuentaDeBaja();
                }
                // SIN PLAN NO SE USA LA APP (Francisco, 29-08). El servidor contesta 402 a
                // todo lo que no sea elegir plan (`core/candado_de_plan`), y sin esto la
                // pantalla se quedaba a medias sin decir por qué. Se le lleva al Inicio, que
                // es donde está la salida: «selecciona un plan». No se cierra la sesión --
                // sigue siendo él y su cuenta está bien -- ni se le echa al login.
                // PERO NO SE LE ECHA DE LA SALIDA (10-09). «Elige tu plan» y la renovación
                // son las dos pantallas a las que hay que ir precisamente cuando no tienes
                // plan. Con la única excepción en `/dashboard`, cualquier 402 que saltara
                // dentro de ellas devolvía al cliente al Inicio, o sea que le cerraba la
                // puerta de comprar: pasó de verdad con el contador de la comunidad, que no
                // estaba en la lista abierta del candado. La causa se arregló, y esto es el
                // cinturón: aunque mañana esas pantallas pidan algo cerrado, no expulsan.
                // Y TAMPOCO DE SU PERFIL (12-10): sin acceso puede abrirlo (lib/puertasDelCliente)
                // y lo que alli le conteste 402 se lo dice su propio aviso, sin sacarlo.
                const SIN_EXPULSAR = [...ABIERTO_SIN_ACCESO, '/planes', '/renovacion'];
                if (error.response?.status === 402
                    && !SIN_EXPULSAR.includes(window.location.pathname)) {
                    window.location.assign('/dashboard');
                }
                return Promise.reject(error);
            }
        );
        return instancia;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `logout` se resuelve al
        // ejecutarse el interceptor, no al crearlo; meterlo aquí volvería a recrear el cliente.
    }, [token, actuandoComoId]);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        
        try {
            // Un reintento si la primera no llega. Conservar el token ya evita tener que
            // volver a escribir la contraseña, pero sin esto la app te deja igualmente en la
            // pantalla de login hasta que recargues. Un corte de un segundo -- entrar en el
            // metro, el servidor terminando de arrancar -- se recupera solo.
            let response;
            try {
                response = await api.get('/auth/me');
            } catch (primera) {
                if (primera.response) throw primera;      // el servidor contestó: no insistas
                await new Promise((r) => setTimeout(r, 1500));
                response = await api.get('/auth/me');
            }
            setUser(response.data);

            // Cargar perfil de cliente si existe (el staff tambien puede tener uno, p.ej. con plan)
            try {
                const profileRes = await api.get('/clients/profile');
                setProfile(profileRes.data);
                // Los ajustes de la ficha (tema, modo de macros, vista) pisan lo que haya
                // en ESTE navegador: es lo que hace que sobrevivan al cambio de móvil
                // (doc 19-08, bloque 07).
                aplicarAjustesDeFicha(profileRes.data?.ajustes_app);
                setPerfilNoCargado(false);
            } catch (e) {
                setProfile(null);
                // 404 = todavía no tiene perfil. Cualquier otra cosa es que no se ha podido
                // traer, y eso hay que decirlo en vez de seguir como si no tuviera plan.
                setPerfilNoCargado(e?.response?.status !== 404);
                if (e?.response?.status !== 404) {
                    console.error('No se pudo cargar el perfil del cliente:', e?.response?.status, e?.message);
                }
            }
        } catch (error) {
            // UN FALLO DE RED NO ES UNA SESIÓN CADUCADA.
            //
            // Aquí se llamaba a `logout()` ante CUALQUIER excepción, y eso incluye el
            // «Network Error» de axios: sin cobertura, wifi que se cae, servidor
            // reiniciándose. O sea que abrir la app en el ascensor te echaba, y no solo eso:
            // `logout()` llama a `limpiarLoDeLaPersona()`, así que además te borraba la copia
            // local del día de Nutrición. Volvías a entrar y te faltaba lo del día.
            //
            // Salió buscando otra cosa: la sesión del entorno local se caía cada dos por tres
            // y parecía que alguien pulsaba «Cerrar sesión». No: era el backend reiniciándose
            // (recarga automática al guardar un fichero) mientras el navegador preguntaba
            // quién eres. En un móvil con mala cobertura pasa exactamente lo mismo.
            //
            // Solo se cierra sesión si el SERVIDOR dice que el token no vale. Los 401 de
            // verdad los recoge además el interceptor de arriba, que avisa al cliente.
            const status = error.response?.status;
            if (status === 401 || status === 403) {
                logout();
            } else {
                // Se conserva el token: al recuperar la conexión, recargar devuelve la sesión
                // sin volver a escribir la contraseña.
                console.error('No se pudo comprobar la sesión (se conserva):', error?.message || error);
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- api recreado cada render; refetch solo en cambio de token
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Catálogo de planes (público): fuente única de habilitaciones/ciclos/precios.
    useEffect(() => {
        axios.get(`${API}/plans`)
            .then((res) => setPlanCatalog(res.data || {}))
            .catch(() => setPlanCatalog({}));
    }, []);

    // Ajustes de la app: con sesión, porque la frase del día es de los clientes. Si no
    // llegan, `pantalla()` cae en null y cada pantalla decide con su propio default.
    const refrescarAjustes = useCallback(async () => {
        if (!token) { setAppSettings(null); return; }
        try {
            const res = await api.get('/settings/app');
            setAppSettings(res.data || {});
        } catch (e) {
            // Vacío, no null: null significa "todavía no sé", y con eso `can()` espera. Si
            // los ajustes no llegan, lo que se sabe es que no hay nada encendido de más.
            setAppSettings({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- api recreado cada render; refetch solo en cambio de token
    }, [token]);

    useEffect(() => { refrescarAjustes(); }, [refrescarAjustes]);

    // pantalla('t3_entreno'): ¿está encendido ese interruptor del panel? Mientras los
    // ajustes no hayan llegado devuelve `porDefecto` (false salvo que se diga otra cosa):
    // mejor no enseñar una pantalla nueva un segundo de más que parpadearla.
    const pantalla = useCallback(
        (nombre, porDefecto = false) => {
            const v = appSettings?.pantallas?.[nombre];
            return v === undefined ? porDefecto : !!v;
        },
        [appSettings]
    );

    // EL SEGUNDO FACTOR DE LOS ADMINS (28-10): con la contraseña bien, un admin no recibe la
    // sesión sino `{segundo_paso, ticket}`, y eso es lo que devuelve aquí en vez del usuario:
    // la pantalla de entrar pide el código (o lo configura) y termina con `segundoFactor`.
    const login = async (email, password) => {
        const response = await api.post('/auth/login', { email, password, dispositivo: dispositivoRecordado(email) });
        if (response.data?.segundo_paso) return { segundoPaso: response.data.segundo_paso, ticket: response.data.ticket };
        const { access_token, user: userData } = response.data;
        return entrarConSesion(access_token, userData);
    };

    // `configurando`: el primer código, que lo activa y trae los códigos de repuesto. La
    // sesión NO se pone aquí sino con `entrar()`: con el usuario puesto, la pantalla de entrar
    // salta al panel y los códigos de repuesto no llegarían a verse.
    const segundoFactor = async (email, ticket, { codigo, repuesto, recordar }, configurando) => {
        const ruta = configurando ? '/auth/segundo-factor/activar' : '/auth/segundo-factor/verificar';
        const response = await api.post(ruta, { ticket, codigo, repuesto, recordar });
        const { access_token, user: userData, dispositivo, codigos_de_repuesto } = response.data;
        recordarDispositivo(email, dispositivo);
        return { entrar: () => entrarConSesion(access_token, userData), codigosDeRepuesto: codigos_de_repuesto || null };
    };

    // Lo que hace entrar con una sesión que ha dado el servidor: la del login y la de quien
    // acaba de pagar el enlace del alta (16-10, `lib/vueltaDelEnlace.vueltaSinSesion`).
    const entrarConSesion = async (access_token, userData) => {
        // EL PERFIL ANTES QUE EL USUARIO (Francisco, 23-09: «al iniciar sesión primero muestra
        // seleccionar plan y luego se actualiza»). Con el usuario puesto y el perfil aún en
        // camino, el Inicio se pintaba un instante sin plan -- «Bienvenido, selecciona un
        // plan» -- y al llegar el perfil saltaba al bueno. Se pide primero y se ponen juntos.
        let perfil = null;
        try {
            const profileRes = await axios.get(`${API}/clients/profile`, {
                headers: { Authorization: `Bearer ${access_token}` }
            });
            perfil = profileRes.data;
            aplicarAjustesDeFicha(perfil?.ajustes_app);
        } catch (e) {
            perfil = null;
        }
        localStorage.setItem('token', access_token);
        setProfile(perfil);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const register = async (data) => {
        const response = await api.post('/auth/register', data);
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const logout = () => {
        // La conversación del chatbot caduca al cerrar sesión: se borra en el backend
        // (mejor esfuerzo: si el token ya expiró, el TTL de 7 días la limpiará igual)
        // y se descarta el snapshot local para que no reaparezca en el próximo login.
        const hadToken = localStorage.getItem('token');
        if (hadToken) {
            try { api.delete('/chatbot/sessions').catch(() => {}); } catch (e) {}
        }
        try { sessionStorage.removeItem('chatbot_session_state'); } catch (e) {}
        // Y TODO LO DEMÁS QUE SEA DE ESTA PERSONA (punto 4.7). No solo la conversación: la
        // copia local del día de Nutrición se quedaba puesta, y el siguiente que entrara en
        // este navegador se encontraba los alimentos del anterior -- y al guardar, se los
        // metía en su propia dieta. Se queda lo que es del aparato (tema, barra lateral).
        limpiarLoDeLaPersona();
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setProfile(null);
    };

    const updateProfile = (newProfile) => {
        setProfile(newProfile);
    };

    const refreshProfile = async () => {
        if (token) {
            try {
                const profileRes = await api.get('/clients/profile');
                setProfile(profileRes.data);
                // Los ajustes de la ficha (tema, modo de macros, vista) pisan lo que haya
                // en ESTE navegador: es lo que hace que sobrevivan al cambio de móvil
                // (doc 19-08, bloque 07).
                aplicarAjustesDeFicha(profileRes.data?.ajustes_app);
                setPerfilNoCargado(false);
            } catch (e) {
                setProfile(null);
                setPerfilNoCargado(e?.response?.status !== 404);
            }
        }
    };

    // Un checkout de Stripe iniciado y nunca pagado deja el perfil en pendiente_pago
    // con checkout_status draft/created: NO cuenta como plan contratado en toda la app.
    // Los perfiles antiguos de pagos manuales no tienen checkout_status y no entran aquí.
    const planUnpaid = !!(profile
        && profile.status === 'pendiente_pago'
        && ['draft', 'created'].includes(profile.checkout_status));

    // Plan del usuario actual (entrada del catálogo), sus habilitaciones y capacidades.
    // La entrada se busca con `planDelCatalogo`, que resuelve mayúsculas y alias igual que
    // el servidor: un plan escrito «CalMa» dejaba al cliente sin ninguna capacidad en la app
    // mientras el backend se las daba todas (ver lib/planAccess.js).
    const myPlan = useMemo(
        () => (profile?.plan && !planUnpaid ? planDelCatalogo(planCatalog, profile.plan) : null),
        [profile?.plan, planUnpaid, planCatalog]
    );
    const habilitaciones = myPlan?.habilitaciones || null;
    // La Rutina depende ademas del interruptor del panel (T3): ver lib/planAccess.js.
    const rutinaVisible = pantalla('t3_entreno');
    // Y la rutina «opcional» (Bronze, Mantenimiento) depende ademas de que la haya comprado
    // ESE cliente: el dato es de su ficha, no del catalogo. Ver lib/planAccess.js.
    const rutinaComprada = !!profile?.rutina_comprada;
    const capabilities = useMemo(
        () => deriveCapabilities(habilitaciones, { rutinaVisible, rutinaComprada }),
        [habilitaciones, rutinaVisible, rutinaComprada]
    );
    // can(cap): ¿el plan del usuario habilita esta capacidad?
    // - MIENTRAS CARGA (perfil o catálogo sin resolver): true, para no parpadear.
    // - CARGADO y sin plan contratado (sin perfil, pago pendiente o plan desconocido):
    //   false. Antes abría por defecto y un registro sin plan veía TODAS las
    //   funcionalidades (rutina, reportes, suplementos...).
    const can = useCallback(
        (cap) => {
            // Los ajustes cuentan como parte de la carga desde que la Rutina depende de
            // uno de ellos: sin esperarlos, entrar directo a /dashboard/entreno rebotaba
            // al panel medio segundo antes de que llegara el interruptor encendido.
            if (loading || !Object.keys(planCatalog).length || appSettings === null) return true;
            if (!myPlan) return false;
            return !!capabilities[cap];
        },
        [loading, planCatalog, myPlan, capabilities, appSettings]
    );

    // Empezar y dejar de actuar como un cliente (punto 4.11). Recarga a propósito: la app
    // entera tiene que recomponerse con el otro usuario -- perfil, macros, capacidades --, y
    // hacerlo a medias es peor que no hacerlo.
    const actuarComo = (cliente) => {
        sessionStorage.setItem('actuar_como', JSON.stringify({
            userId: cliente.userId, nombre: cliente.nombre, clientId: cliente.clientId,
            // Quién actúa (12-10): un entrenador no toca el plan, los cobros, la baja, la
            // contraseña ni el correo del cliente. Manda el servidor (`core/security`); esto
            // es para no enseñarle botones que le van a contestar que no.
            porRol: user?.role,
            // Y quién es (14-10): entrar en la propia cuenta no es actuar por otro
            // (`lib/cabeceras.actuaPorOtro`, que cierra el chat).
            porId: user?.id,
        }));
        window.location.assign('/dashboard/nutrition');
    };
    const dejarDeActuar = () => {
        const vuelta = actuandoComo?.clientId ? `/admin/clients/${actuandoComo.clientId}` : '/admin/clients';
        sessionStorage.removeItem('actuar_como');
        window.location.assign(vuelta);
    };

    // MIENTRAS LA APP ESTÁ CERRADA, SE PREGUNTA CADA MEDIO MINUTO (14-09). Sin esto, el
    // cliente que dejó la app abierta se queda con la pantalla de «volvemos enseguida»
    // hasta que se le ocurra recargar, y en el móvil eso puede ser al día siguiente. La
    // primera respuesta buena la quita el interceptor, así que aquí basta con preguntar.
    useEffect(() => {
        if (!puertaCerrada || !token) return undefined;
        const id = setInterval(() => {
            api.get('/auth/me').catch(() => {});
        }, 30000);
        return () => clearInterval(id);
    }, [puertaCerrada, token, api]);

    const value = {
        user,
        token,
        profile,
        perfilNoCargado,
        loading,
        login,
        segundoFactor,
        entrarConSesion,
        register,
        logout,
        actuandoComo,
        // Se está en la cuenta de un cliente y quien actúa no es administrador: lo del
        // contrato y la llave de la cuenta no se le ofrece.
        actuaSinSerAdmin: !!actuandoComo?.userId && actuandoComo?.porRol !== 'admin',
        actuarComo,
        dejarDeActuar,
        updateProfile,
        refreshProfile,
        refreshUser: fetchUser,
        api,
        planCatalog,
        appSettings,
        refrescarAjustes,
        pantalla,
        myPlan,
        planUnpaid,
        habilitaciones,
        capabilities,
        can,
        isAuthenticated: !!token && !!user,
        isAdmin: user?.role === 'admin',
        isTrainer: user?.role === 'trainer',
        isClient: user?.role === 'client'
    };

    return (
        <AuthContext.Provider value={value}>
            {puertaCerrada
                ? <PuertaCerrada mensaje={typeof puertaCerrada === 'string' ? puertaCerrada : null} />
                : children}
        </AuthContext.Provider>
    );
};
